<script setup>
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AppImage from './ui/AppImage.vue'
import LanguageSwitcher from './ui/LanguageSwitcher.vue'
import { brand, primaryNav } from '@/data/content'
import { useMediaQuery } from '@/composables/useMediaQuery'
import { useScrolledPast } from '@/composables/useScrolledPast'
import { useDemoModal } from '@/composables/useDemoModal'

const { t } = useI18n()
const { open: openDemo } = useDemoModal()

const menuOpen = ref(false)
const isWide = useMediaQuery('(min-width: 900px)')
const scrolled = useScrolledPast(24)

// The desktop nav is always visible, so a stale "open" flag must not survive
// a resize — support.js solved this by rewriting cssText on every resize.
watch(isWide, (wide) => {
  if (wide) menuOpen.value = false
})

const closeMenu = () => {
  menuOpen.value = false
}

// Close the mobile menu first, or the dialog opens behind an open drawer.
const requestDemo = () => {
  closeMenu()
  openDemo()
}
</script>

<template>
  <header class="pointer-events-none fixed inset-x-0 top-0 z-50 flex justify-center px-5 py-[14px]">
    <nav
      :aria-label="t('ui.nav.primary')"
      class="pointer-events-auto relative flex w-full max-w-shell items-center gap-7 rounded-[14px] border px-[18px] py-3 transition-[background-color,border-color,box-shadow] duration-300"
      :class="
        scrolled
          ? 'nav-frost border-ink/10 bg-canvas/80 shadow-[0_18px_40px_-34px_rgba(16,22,28,.6)] backdrop-blur-[14px]'
          : 'border-transparent bg-transparent'
      "
    >
      <a href="#top" class="flex items-center gap-[10px] text-ink" @click="closeMenu">
        <AppImage
          :src="brand.logo"
          :alt="brand.name"
          :width="brand.logoSize"
          :height="brand.logoSize"
          loading="eager"
          class="h-[34px] w-[34px] object-contain"
        />
        <span class="font-display text-lg font-bold tracking-[0.14em]">{{ brand.name }}</span>
      </a>

      <div class="ml-auto flex items-center gap-1">
        <!-- One list serves both layouts; only the presentation changes. -->
        <ul
          v-show="isWide || menuOpen"
          id="primary-menu"
          class="list-none"
          :class="
            isWide
              ? 'flex flex-row gap-[2px] p-0'
              : 'absolute left-2 right-2 top-full mt-[10px] flex flex-col gap-[2px] rounded-[14px] border border-ink/10 bg-white p-[10px] shadow-[0_24px_50px_-30px_rgba(16,22,28,.5)]'
          "
        >
          <li v-for="item in primaryNav" :key="item.key">
            <a
              :href="item.href"
              class="flex min-h-[44px] items-center rounded-lg px-3 py-2 text-sm font-medium text-muted transition-colors hover:bg-canvas-alt hover:text-ink lg:min-h-0"
              @click="closeMenu"
            >
              {{ t(`ui.nav.items.${item.key}`) }}
            </a>
          </li>
        </ul>

        <!-- Outside the collapsible list on purpose: the language toggle must
             stay reachable on phones without opening the menu first. -->
        <LanguageSwitcher class="mr-1" />

        <button
          v-if="!isWide"
          type="button"
          class="grid h-10 w-10 place-items-center rounded-[10px] border border-hairline bg-white"
          :aria-label="t('ui.nav.toggle')"
          aria-controls="primary-menu"
          :aria-expanded="menuOpen ? 'true' : 'false'"
          @click="menuOpen = !menuOpen"
        >
          <span aria-hidden="true" class="burger-bars" />
        </button>

        <button
          type="button"
          class="ml-[10px] hidden rounded-[10px] bg-ink px-[18px] py-[10px] text-sm font-semibold text-white transition-colors hover:bg-brand-deep focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-deep sm:block"
          @click="requestDemo"
        >
          {{ t('ui.actions.requestDemo') }}
        </button>
      </div>
    </nav>
  </header>
</template>
